import Ticket from "@/assets/icons/Ticket";
import { useAuth } from "@/auth/Auth";
import { usePermissions } from "@/auth/PermissionsContext";
import DialogDelete from "@/components/shared/DialogDelete/DialogDelete";
import Divider from "@/components/shared/Divider/Divider";
import CustomSheet from "@/components/shared/Sheet/Sheet";
import CTATable from "@/components/shared/TableListCustom/CTATable";
import TableListCustom from "@/components/shared/TableListCustom/TableListCustom";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { TableCell, TableRow } from "@/components/ui/table";
import { toast } from "@/hooks/use-toast";
import LayoutInside from "@/layout/LayoutInside";
import { allIntegrations } from "@/pages/Integrations/Integrations";
import { IntegrationsService } from "@/services/integrations.service";
import useModalStore from "@/store/useModalStore";
import {
  invalidateWmsProductOptions,
  isWmsIntegration,
} from "@/utils/integrationProductFilters";
import { isStaging } from "@/utils/isStaging";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Edit, Plus, Trash } from "lucide-react";
import { ReactNode, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import GenericCreateIntegration from "../GenericCreateIntegration/GenericCreateIntegration";
import GenericEditIntegration from "../GenericEditIntegration/GenericEditIntegration";

type IntegrationData = {
  id: string;
  name: string;
  access_token?: string;
  postback_url?: string;
  status: boolean;
  [key: string]: any;
};

type Field = {
  key: string;
  render?: (item: IntegrationData) => ReactNode;
};

export type FixedOptions = {
  payment_methods?: string[];
  active_statuses?: string[];
};

export type FixedValues = {
  payment_methods?: string[];
  active_statuses?: string[];
  metadata?: string[];
  products?: string[];
};

export type FieldLabels = {
  title?: string;
  access_token?: string;
  postback_url?: string;
  products?: string;
  payment_status?: string;
  payment_type?: string;
  [key: string]: string | undefined;
};

type Props = {
  integrationKey: string;
  title: string;
  icon: ReactNode;
  main_icon: ReactNode;
  fields: Field[];
  fixedOptions?: FixedOptions;
  fixedValues?: FixedValues;
  fieldLabels?: FieldLabels;
};

export default function IntegrationTable({
  integrationKey,
  title,
  main_icon,
  icon,
  fields,
  fixedOptions,
  fixedValues,
  fieldLabels = {},
}: Props) {
  const { t } = useTranslation(["integration", "common"]);
  const { modals, openModal, closeModal } = useModalStore();
  const queryClient = useQueryClient();
  const [selectedIntegration, setSelectedIntegration] =
    useState<IntegrationData | null>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const navigate = useNavigate();
  const { authData } = useAuth();
  const [copied, setCopied] = useState(false);
  const editModalKey = `edit-${integrationKey}`;
  const wasEditOpenRef = useRef(Boolean(modals[editModalKey]));

  const { hasPermission } = usePermissions();
  const canCreate = hasPermission('integrations.create_integration');
  const canEdit   = hasPermission('integrations.edit_integration');
  const canDelete = hasPermission('integrations.delete_integration');

  useEffect(() => {
    const currentIntegration = allIntegrations.find((integration) => {
      const integrationRoute = integration.route.replace("/", "");
      const currentRoute =
        integrationKey === "utmfy" ? "utmify" : integrationKey;

      return (
        integrationRoute === currentRoute ||
        integrationRoute === integrationKey ||
        (integrationKey === "utmify" && integrationRoute === "utmfy") ||
        (integrationKey === "ship_offers" && integrationRoute === "ship-offers")
      );
    });

    if (currentIntegration && currentIntegration.disabled && !isStaging) {
      navigate("/integrations", { replace: true });
      toast({
        variant: "error",
        description: t("common:data_listing_error"),
      });
    }
  }, [integrationKey, navigate, t]);

  const items = [
    { text: t("integration:integrations"), link: "/integrations/configurations" },
    {
      text: title,
      link: `/integrations/${
        integrationKey === "utmfy" ? "utmify" : integrationKey
      }`,
    },
  ];

  const {
    data: integrations,
    isPending: isLoading,
    isError,
    error,
    refetch,
  } = useQuery({
    queryKey: [integrationKey, currentPage],
    queryFn: async () =>
      await IntegrationsService.getIntegration({
        integration: integrationKey === "utmify" ? "utmfy" : integrationKey,
        page: currentPage,
      }),
    retry: false,
  });

  const handleDelete = async (id: string) => {
    try {
      await IntegrationsService.deleteIntegration(id);
      toast({
        variant: "success",
        description: t("integration:integration_deleted_successfully"),
      });
      await refetch();
      if (isWmsIntegration(integrationKey)) {
        await invalidateWmsProductOptions(queryClient);
      }
      setSelectedIntegration(null);
      closeModal("delete-integration");
    } catch (error: any) {
      toast({
        variant: "error",
        description:
          error?.response?.data?.message ||
          t("integration:error_deleting_integration"),
      });
    }
  };

  useEffect(() => {
    const isEditOpen = Boolean(modals[editModalKey]);
    if (wasEditOpenRef.current && !isEditOpen) {
      setSelectedIntegration(null);
    }
    wasEditOpenRef.current = isEditOpen;
  }, [modals, editModalKey]);

  const headers = [
    { title: t("integration:name"), key: "name" },
    ...fields
      .filter((field) => field.key !== "metadata")
      .map((field) => ({
        title:
          fieldLabels[field.key] || t(`integration:${field.key}`) || field.key,
        key: field.key,
      })),
    { title: "", key: "actions" },
  ];

  const emptyResponse = {
    data: [] as IntegrationData[],
    meta: { last_page: 1 },
    links: {} as { next?: string; prev?: string },
  };
  const displayData =
    isError || !integrations
      ? emptyResponse
      : integrations;

  useEffect(() => {
    if (isError && error) {
      const message =
        (error as { response?: { data?: { message?: string } } })?.response
          ?.data?.message || t("common:data_listing_error");
      toast({
        variant: "error",
        description: message,
      });
    }
  }, [isError, error, t]);

  return (
    <LayoutInside hasBgBlur items={items}>
      <CustomSheet
        open={modals[`create-${integrationKey}`]}
        onClose={() => closeModal(`create-${integrationKey}`)}
      >
        <GenericCreateIntegration
          integrationKey={integrationKey}
          modalKey={`create-${integrationKey}`}
          fields={fields.map((f) => f.key as "access_token" | "postback_url" | "metadata" | "secret_token")}
          icon={icon}
          title={title}
          fixedOptions={fixedOptions}
          fixedValues={fixedValues}
          fieldLabels={fieldLabels}
        />
      </CustomSheet>

      <CustomSheet
        open={modals[editModalKey]}
        onClose={() => {
          closeModal(editModalKey);
          setSelectedIntegration(null);
        }}
      >
        {selectedIntegration && (
          <GenericEditIntegration
            key={selectedIntegration.id}
            integrationKey={integrationKey}
            modalKey={editModalKey}
            fields={fields.map((f) => f.key as "access_token" | "postback_url" | "metadata" | "secret_token")}
            integration={selectedIntegration}
            icon={icon}
            title={title}
            fixedOptions={fixedOptions}
            fixedValues={fixedValues}
            fieldLabels={fieldLabels}
          />
        )}
      </CustomSheet>

      <section className="flex flex-col mb-8 items-center">
        <div>{main_icon}</div>
        <h1 className="font-semibold text-2xl mt-3">{`Mundpay + ${title}`}</h1>
      </section>

      <Card className="w-full mt-12 overflow-x-auto">
        <div className="flex md:items-center flex-col md:flex-row w-full p-4 justify-between max-md:gap-3">
          <h1 className="text-base font-semibold">{t("integrations")}</h1>
          {canCreate && (
            <Button
              className="md:w-fit"
              onClick={() => openModal(`create-${integrationKey}`)}
              disabled={isLoading}
              data-cy="create-integration-button"
            >
              <Plus />
              {t("common:connect_integration")}
            </Button>
          )}
        </div>

        <Divider />

        <TableListCustom
          headers={headers}
          isLoadingData={isLoading}
          data={displayData}
          currentPage={currentPage}
          setCurrentPage={setCurrentPage}
          emptyTitleMessage={
            isError
              ? t("common:data_listing_error")
              : t("common:emptyIntegrations.title")
          }
          emptyDescriptionMessage={
            isError
              ? ""
              : t("common:emptyIntegrations.description")
          }
          emptyIconMessage={<Ticket />}
        >
          {displayData?.data?.map((item: IntegrationData) => (
            <TableRow
              key={item.id}
              data-cy="table-row"
              className="h-[64px] border-b border-b-colorBorder hover:bg-transparent"
            >
              <TableCell className="font-medium">{item.name}</TableCell>
              {fields
                .filter((field) => field.key !== "metadata")
                .map((field) => (
                  <TableCell key={field.key}>
                    {field.render ? field.render(item) : item[field.key]}
                  </TableCell>
                ))}
              <TableCell className="w-[50px]">
                <CTATable
                  items={[
                    ...(canEdit ? [{
                      id: 1,
                      label: t("integration:edit"),
                      icon: <Edit />,
                      action: () => {
                        setSelectedIntegration(item);
                        openModal(`edit-${integrationKey}`);
                      },
                    }] : []),
                    ...(canDelete ? [{
                      id: 2,
                      label: t("integration:delete"),
                      icon: <Trash className="text-red-400" />,
                      action: () => {
                        setSelectedIntegration(item);
                        openModal("delete-integration");
                      },
                    }] : []),
                  ]}
                />
              </TableCell>
            </TableRow>
          ))}
          {selectedIntegration && (
            <DialogDelete
              isOpen={modals["delete-integration"]}
              onClose={() => closeModal("delete-integration")}
              onDelete={async () => {
                await handleDelete(selectedIntegration.id);
                closeModal("delete-integration");
              }}
              title={t("integration:delete_offer_title")}
              description={t("integration:delete_offer_description")}
            />
          )}
        </TableListCustom>
      </Card>
    </LayoutInside>
  );
}
